// Edits of a project, as pure functions: what each button of the Data & sets module does
// to the project, ported from DipField 1.0.0 (same rules, so results match). The app calls
// them after store.pushHistory(); the parity tests call them directly. GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import type { JointSet, Project, TraverseDef } from "@dipfield/project";
import { anyBiased, type DseResult } from "./derived.js";

const stampNow = () => Date.now().toString(36);
const spotT1 = (): TraverseDef => ({ id: "T1", name: "T1", tr: { kind: "spot" } });

/** Rows from a table import. Like DIPS, the project then shows data in the imported
 *  format. Traverse names in the file become spot traverses (set their kind later).
 *  Sets are kept: their windows apply to the new data. */
export function applyImport(p: Project, planes: S.Plane[], convention: S.OrientationFormat, replace: boolean, stamp = stampNow()) {
  const d = p.data;
  d.format = convention;
  if (replace) d.traverses = [spotT1()];
  const byName = new Map(d.traverses.map((t) => [t.name, t.id] as [string, string]));
  for (const q of planes) if (q.traverse && !byName.has(q.traverse)) {
    const id = `tv-${q.traverse}-${stamp}`; d.traverses.push({ id, name: q.traverse, tr: { kind: "spot" } }); byName.set(q.traverse, id);
  }
  // if every row names a traverse, drop the unused default T1
  if (replace && planes.length && planes.every((q) => q.traverse) && d.traverses.length > 1) d.traverses.shift();
  d.planes = (replace ? [] : d.planes).concat(planes.map((q) => ({ dip: q.dip, dipDir: q.dipDir, quantity: q.quantity,
    ...(q.traverse ? { traverse: byName.get(q.traverse)! } : {}) })));
}

/** The scanline field template: one linear traverse per scanline, measurements on them. */
export function applyTemplate(p: Project, t: S.ScanlineImport, useWeights: boolean, spotName: string) {
  const d = p.data;
  d.format = "dip-dipdir"; p.sets = [];
  d.traverses = t.scanlines.map((sl) => ({ id: `sl-${sl.id}`, name: sl.id, tr: { kind: "linear", trend: sl.trend, plunge: sl.plunge } }));
  const hasSpot = t.planes.some((q) => !q.traverse);
  if (hasSpot || !d.traverses.length) d.traverses.unshift({ id: "T1", name: spotName, tr: { kind: "spot" } });
  d.planes = t.planes.map((q) => ({ dip: q.dip, dipDir: q.dipDir, quantity: 1, traverse: q.traverse ? `sl-${q.traverse}` : "T1" }));
  d.terzaghi.use = useWeights;
}

/** A sample data set replaces everything: rows, sets and traverses. */
export function applySample(p: Project, csv: string) {
  const t = S.parseTable(csv), m = S.guessMapping(t);
  p.sets = []; p.data.traverses = [spotT1()]; p.data.terzaghi.use = false;
  p.data.format = m.convention;
  p.data.planes = S.toPlanes(t, m).planes.map((q) => ({ dip: q.dip, dipDir: q.dipDir, quantity: q.quantity }));
}

export function clearData(p: Project) { p.data.planes = []; p.sets = []; }

// ---------- sets ----------
export function nextSetName(p: Project) {
  const used = new Set(p.sets.map((x) => x.name)); let k = 1; while (used.has(`J${k}`)) k++;
  return `J${k}`;
}
export function addSet(p: Project, window: S.SetWindow, stamp = stampNow()) {
  const name = nextSetName(p);
  p.sets.push({ id: `${name}-${stamp}`, name, window, friction: null });
}
/** DSE replaces every set with circular windows of the cone radius around the peaks. The
 *  joint strength of set k (Slope module) carries over to the new set k, as in DipField Slope. */
export function applyDse(p: Project, r: DseResult, coneDeg: number, stamp = stampNow()) {
  const strength = p.sets.map((s) => p.slope.materials[s.id]);
  p.sets = r.poles.map((q, k): JointSet => ({ id: `J${k + 1}-${stamp}`, name: `J${k + 1}`,
    window: { kind: "circle", axis: q.normal, radius: coneDeg }, friction: null }));
  p.sets.forEach((s, k) => { if (strength[k]) p.slope.materials[s.id] = { ...strength[k] }; });
}
/** A typed value in a set card's window editor. Keys: circle trend / plunge / radius;
 *  curved t1, t2 (trend from, to) and p1, p2 (plunge min, max). */
export function editWindow(set: JointSet, key: string, v: number) {
  const w = set.window;
  if (w.kind === "circle") {
    const l = S.vecToLine(w.axis);
    if (key === "radius") w.radius = Math.max(0.5, Math.min(90, v));
    else w.axis = S.lineToVec(key === "trend" ? S.wrap360(v) : l.trend, key === "plunge" ? Math.max(0, Math.min(90, v)) : l.plunge);
  } else if (w.kind === "curved") {
    const t2 = S.wrap360(w.trendStart + w.trendWidth);
    if (key === "t1") { w.trendStart = S.wrap360(v); w.trendWidth = S.wrap360(t2 - w.trendStart); }
    if (key === "t2") w.trendWidth = S.wrap360(S.wrap360(v) - w.trendStart);
    if (key === "p1") w.plungeMin = Math.max(-30, Math.min(w.plungeMax, v));
    if (key === "p2") w.plungeMax = Math.min(90, Math.max(w.plungeMin, v));
  }
}

// ---------- traverses ----------
export function setTraverseKind(p: Project, i: number, kind: S.Traverse["kind"]) {
  p.data.traverses[i].tr = kind === "spot" ? { kind: "spot" } : kind === "linear" ? { kind: "linear", trend: 0, plunge: 0 } : { kind: "planar", dip: 90, dipDir: 0 };
  if (!anyBiased(p)) p.data.terzaghi.use = false;
}
/** `which` a: trend (linear) or dip (planar); b: plunge or dip direction. */
export function setTraverseValue(p: Project, i: number, which: "a" | "b", v: number) {
  const tr = p.data.traverses[i].tr;
  if (!Number.isFinite(v)) return;
  if (tr.kind === "linear") { if (which === "a") tr.trend = S.wrap360(v); else tr.plunge = Math.max(-90, Math.min(90, v)); }
  if (tr.kind === "planar") { if (which === "a") tr.dip = Math.max(0, Math.min(90, v)); else tr.dipDir = S.wrap360(v); }
}
export function addTraverse(p: Project, stamp = stampNow()) {
  let k = p.data.traverses.length + 1; while (p.data.traverses.some((t) => t.name === `T${k}`)) k++;
  p.data.traverses.push({ id: `T${k}-${stamp}`, name: `T${k}`, tr: { kind: "linear", trend: 0, plunge: 0 } });
}
export function removeTraverse(p: Project, i: number) {
  const gone = p.data.traverses[i].id;
  p.data.traverses.splice(i, 1);
  p.data.planes.forEach((q) => { if (q.traverse === gone) q.traverse = p.data.traverses[0].id; });
  if (!anyBiased(p)) p.data.terzaghi.use = false;
}
export function setTerzaghi(p: Project, minBiasDeg: number, use: boolean) {
  p.data.terzaghi.minBiasDeg = Number.isFinite(minBiasDeg) ? Math.max(1, Math.min(45, minBiasDeg)) : 15;
  p.data.terzaghi.use = anyBiased(p) && use;
}
/** Rows on a traverse; rows naming no known traverse count on the first (TRZ-04). */
export const rowsOnTraverse = (p: Project, id: string, first: boolean) =>
  p.data.planes.filter((q) => (q.traverse ?? "") === id || (first && !p.data.traverses.some((t) => t.id === q.traverse))).length;
